import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  LinearProgress,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import PeopleIcon from '@mui/icons-material/People';
import LinkIcon from '@mui/icons-material/Link';
import BadgeIcon from '@mui/icons-material/Badge';
import GroupsIcon from '@mui/icons-material/Groups';
import { useVolunteer } from '../../contexts/VolunteerContext';
import { formatDate } from '../../utils/helpers';
import { ROUTES } from '../../utils/constants';
import { VolunteerShift, VolunteerShiftDetail } from '../../types/volunteer';
import { getShiftDetail } from '../../services/volunteerApi';
import ShiftCheckInActions from '../../components/volunteers/ShiftCheckInActions';
import { volunteerErrorMessage } from '../../utils/volunteerErrors';

const VolunteerShiftDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    status,
    shifts,
    myShifts,
    loading,
    error,
    fetchStatus,
    fetchShifts,
    fetchMyShifts,
    registerForShift,
    cancelShift,
  } = useVolunteer();

  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  // The shift itself, read by id: it works for a past shift (the list only holds today onwards)
  // and carries the team when you are signed up to it.
  const [detail, setDetail] = useState<VolunteerShiftDetail | null>(null);
  const loadDetail = useCallback(async () => {
    if (!id) return;
    try {
      setDetail((await getShiftDetail(id)).data);
    } catch {
      setDetail(null);
    }
  }, [id]);

  useEffect(() => {
    fetchStatus();
    fetchShifts();
    fetchMyShifts();
    loadDetail();
  }, [fetchStatus, fetchShifts, fetchMyShifts, loadDetail]);

  // Redirect if not approved
  useEffect(() => {
    if (status && status.volunteerStatus !== 'approved') {
      navigate(ROUTES.VOLUNTEERS);
    }
  }, [status, navigate]);

  const shift: VolunteerShift | undefined = useMemo(
    () => detail ?? shifts.find((s) => s.id === id),
    [detail, shifts, id]
  );

  const existingRegistration = useMemo(
    () => myShifts.find((r) => r.shiftId === id && r.status === 'confirmed'),
    [myShifts, id]
  );

  const handleRegister = async () => {
    if (!id) return;

    setIsSubmitting(true);
    setActionError(null);

    try {
      await registerForShift(id, selectedRoleId || undefined);
      setSuccessMessage('Successfully registered for this shift!');
      setShowSuccess(true);
      // Refresh data
      await fetchShifts();
      await fetchMyShifts();
      await loadDetail();
    } catch (err: any) {
      setActionError(volunteerErrorMessage(err, 'Failed to register for shift'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;

    setIsSubmitting(true);
    setActionError(null);

    try {
      await cancelShift(id);
      setSuccessMessage('Shift registration cancelled');
      setShowSuccess(true);
      // Refresh data
      await fetchShifts();
      await fetchMyShifts();
      await loadDetail();
    } catch (err: any) {
      setActionError(volunteerErrorMessage(err, 'Failed to cancel registration'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && !shifts.length && !detail) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!shift) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Shift not found
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(ROUTES.VOLUNTEER_SHIFTS)}
        >
          Back to Shifts
        </Button>
      </Box>
    );
  }

  const capacityPercent =
    shift.capacity > 0
      ? Math.min((shift.currentVolunteers / shift.capacity) * 100, 100)
      : 0;
  const isFull = shift.currentVolunteers >= shift.capacity;
  // A past or cancelled shift can now be opened (by id); it cannot be signed up to.
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const isClosed = !shift.isActive || new Date(shift.date) < todayStart;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(ROUTES.VOLUNTEER_SHIFTS)}
        sx={{ mb: 3 }}
      >
        Back to Shifts
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Shift Details */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              height: 200,
              backgroundColor: shift.eventId ? '#9c27b0' : '#00b7ed',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <EventIcon sx={{ fontSize: 80, color: 'white' }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              label={shift.eventId ? 'Event-linked' : 'Standalone'}
              sx={{
                backgroundColor: shift.eventId ? '#9c27b0' : '#00b7ed',
                color: 'white',
              }}
            />
            {isFull && <Chip label="FULL" color="error" />}
          </Box>

          <Typography variant="h4" fontWeight={600} gutterBottom>
            {shift.title}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon color="action" />
              <Typography>{formatDate(shift.date)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon color="action" />
              <Typography>
                {shift.startTime} - {shift.endTime}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon color="action" />
              <Typography>
                {shift.location}
                {shift.postcode && ` (${shift.postcode})`}
              </Typography>
            </Box>
            {shift.postcode && (
              <Button
                size="small"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shift.postcode)}`}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<MapIcon />}
              >
                View on Map
              </Button>
            )}
          </Box>

          <Typography variant="h6" fontWeight={600} gutterBottom>
            About this shift
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {shift.description}
          </Typography>

          {shift.event && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon color="action" />
                <Typography variant="body1">
                  Linked to event:{' '}
                  <Button
                    size="small"
                    onClick={() =>
                      navigate(
                        ROUTES.EVENT_DETAIL.replace(':id', shift.event!.id)
                      )
                    }
                  >
                    {shift.event.title}
                  </Button>
                </Typography>
              </Box>
            </Box>
          )}

          {/* Available Roles */}
          {shift.roles.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Available Roles
              </Typography>
              <List
                sx={{
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                }}
              >
                {shift.roles.map((role) => (
                  <ListItem key={role.id}>
                    <BadgeIcon sx={{ mr: 1.5, color: '#00b7ed' }} />
                    <ListItemText
                      primary={role.name}
                      secondary={
                        role.capacity
                          ? `${role.capacity} position${role.capacity !== 1 ? 's' : ''}`
                          : undefined
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {detail?.team && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupsIcon sx={{ color: '#00b7ed' }} />
                Who's on this shift ({detail.team.length})
              </Typography>
              <List sx={{ bgcolor: '#f5f5f5', borderRadius: 2 }}>
                {detail.team.map((member) => (
                  <ListItem key={member.id}>
                    <ListItemText
                      primary={member.isYou ? `${member.name} (you)` : member.name}
                      secondary={member.role ?? undefined}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="caption" color="text.secondary">
                Only volunteers signed up to this shift can see this list.
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Registration Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 80 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Registration
              </Typography>

              {/* Capacity indicator */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon fontSize="small" />
                    <Typography variant="body2">Capacity</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {shift.currentVolunteers} / {shift.capacity}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={capacityPercent}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor:
                        capacityPercent >= 90
                          ? '#f44336'
                          : capacityPercent >= 70
                          ? '#ff9800'
                          : '#4caf50',
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {shift.capacity - shift.currentVolunteers} spots remaining
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {actionError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {actionError}
                </Alert>
              )}

              {existingRegistration ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    You are registered for this shift.
                    {existingRegistration.role && (
                      <> Role: {existingRegistration.role.name}</>
                    )}
                  </Alert>
                  {existingRegistration.checkedIn && existingRegistration.checkInTime && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Checked in at{' '}
                      {new Date(existingRegistration.checkInTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      {existingRegistration.checkOutTime && (
                        <>
                          , checked out at{' '}
                          {new Date(existingRegistration.checkOutTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </>
                      )}
                    </Typography>
                  )}
                  <Box sx={{ mb: 2 }}>
                    <ShiftCheckInActions
                      registration={{ ...existingRegistration, shift: existingRegistration.shift ?? shift }}
                      onChanged={async (message) => {
                        setSuccessMessage(message);
                        setShowSuccess(true);
                        await fetchMyShifts();
                      }}
                    />
                  </Box>
                  {!existingRegistration.checkedIn && (
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Cancelling...' : 'Cancel Registration'}
                    </Button>
                  )}
                </Box>
              ) : isClosed ? (
                <Alert severity="info">
                  {shift.isActive ? 'This shift has finished.' : 'This shift has been cancelled.'}
                </Alert>
              ) : isFull ? (
                <Alert severity="warning">
                  This shift is fully booked.
                </Alert>
              ) : (
                <Box>
                  {shift.roles.length > 0 && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Select a Role (optional)</InputLabel>
                      <Select
                        value={selectedRoleId}
                        label="Select a Role (optional)"
                        onChange={(e) => setSelectedRoleId(e.target.value)}
                      >
                        <MenuItem value="">No specific role</MenuItem>
                        {shift.roles.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleRegister}
                    disabled={isSubmitting}
                    sx={{
                      backgroundColor: '#00b7ed',
                      '&:hover': { backgroundColor: '#009fcc' },
                    }}
                  >
                    {isSubmitting ? 'Registering...' : 'Register for Shift'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VolunteerShiftDetailPage;
